<template>
  <AuthSplitLayout
    :hero-title="$t('auth.layout.heroTitleLogin')"
    :hero-subtitle="$t('auth.layout.heroSubLogin')"
    :locale="$i18n.locale"
    @lang="switchLanguage"
  >
    <h1 class="auth-title">{{ $t('auth.loginWelcomeTitle') }}</h1>
    <p v-if="sessionExpired" class="session-expired-banner" role="alert">
      {{ $t('auth.sessionExpired') }}
    </p>
    <form class="auth-form" @submit.prevent="login">
      <label class="auth-label">{{ $t('auth.emailLabel') }}</label>
      <input
        v-model="username"
        type="email"
        autocomplete="username email"
        :placeholder="$t('auth.emailLabel')"
        required
      />
      <label class="auth-label">{{ $t('auth.password') }}</label>
      <input
        v-model="password"
        type="password"
        autocomplete="current-password"
        :placeholder="$t('auth.password')"
        required
      />
      <button type="submit" class="auth-btn auth-btn--primary">{{ $t('auth.loginButton') }}</button>
    </form>
    <p class="auth-switch">
      {{ $t('auth.noAccount') }}
      <router-link :to="registerLink">{{ $t('auth.register') }}</router-link>
    </p>
    <div class="auth-divider">
      <span>{{ $t('auth.or') }}</span>
    </div>
    <div v-if="googleClientId" ref="googleMount" class="google-mount" />
    <p v-else class="auth-hint-muted">{{ $t('auth.googleUnavailable') }}</p>
    <div class="forgot-password-toggle">
      <button type="button" class="link-button" @click="showHelp = !showHelp">
        {{ $t('auth.forgotPassword') }}
      </button>
      <div v-if="showHelp" class="forgot-password-block">
        <p>
          {{ $t('auth.contactUs') }}
          <a :href="'mailto:' + $t('contact.email')">{{ $t('contact.email') }}</a>,
          {{ $t('auth.weWillHelp') }}
        </p>
      </div>
    </div>
  </AuthSplitLayout>
</template>

<script>
import { useAuthStore } from '@/stores/auth';
import AuthSplitLayout from '@/components/AuthSplitLayout.vue';
import { getGoogleClientId, mountGoogleSignInButton } from '@/utils/googleSignIn';
import { getSafeNextLocation } from '@/utils/postAuthRedirect';

export default {
  components: { AuthSplitLayout },
  data() {
    return {
      username: '',
      password: '',
      showHelp: false,
      googleClientId: getGoogleClientId(),
    };
  },
  computed: {
    sessionExpired() {
      return this.$route.query.expired === '1';
    },
    registerLink() {
      const safe = getSafeNextLocation(this.$route.query.next);
      if (safe) return { path: '/register', query: { next: safe } };
      return '/register';
    },
  },
  async mounted() {
    await this.$nextTick();
    await this.setupGoogleButton();
  },
  methods: {
    redirectAfterAuth() {
      const dest = getSafeNextLocation(this.$route.query.next);
      this.$router.push(dest || '/new/dashboard');
    },
    async setupGoogleButton() {
      if (!this.googleClientId || !this.$refs.googleMount) return;
      try {
        await mountGoogleSignInButton(this.$refs.googleMount, {
          locale: this.$i18n.locale,
          onCredential: this.onGoogleCredential,
        });
      } catch (e) {
        console.warn('Google Sign-In button:', e);
      }
    },
    async onGoogleCredential(response) {
      const credential = response?.credential;
      if (!credential) return;
      try {
        const res = await fetch('/api/auth/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential, mode: 'login' }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.access_token) {
          const authStore = useAuthStore();
          authStore.login(data.access_token);
          this.redirectAfterAuth();
          return;
        }
        if (res.status === 503) {
          const parts503 = [data.hint, data.verify_message, data.message, data.error].filter(Boolean);
          alert(
            parts503.length ? parts503.join('\n\n') : this.$t('auth.googleServerDisabled'),
          );
          return;
        }
        if (data.error === 'not_registered') {
          alert(this.$t('auth.googleNotRegistered'));
          return;
        }
        if (res.status === 401 && data.verify_message) {
          console.error('[Google auth]', data.verify_message);
        }
        const parts = [data.hint, data.verify_message, data.message, data.error].filter(Boolean);
        alert(parts.length ? parts.join('\n\n') : this.$t('auth.loginError'));
      } catch (e) {
        console.error(e);
        alert(this.$t('auth.loginError'));
      }
    },
    async login() {
      try {
        const response = await fetch('/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: this.username, password: this.password }),
        });
        const data = await response.json();
        if (response.ok) {
          const authStore = useAuthStore();
          authStore.login(data.access_token);
          this.redirectAfterAuth();
        } else {
          alert(data.message || data.error || this.$t('auth.loginError'));
        }
      } catch (error) {
        console.error('Ошибка входа:', error);
      }
    },
    switchLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem('language', lang);
      this.$nextTick(() => this.setupGoogleButton());
    },
  },
};
</script>

<style scoped>
.auth-title {
  margin: 0 0 24px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #111827;
}

.session-expired-banner {
  background: #fffbeb;
  color: #92400e;
  padding: 12px 14px;
  border-radius: 10px;
  margin: 0 0 18px;
  font-size: 14px;
  line-height: 1.45;
  text-align: left;
  border: 1px solid #fde68a;
}

.auth-form {
  text-align: left;
}

.auth-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.auth-form input {
  width: 100%;
  padding: 14px 16px;
  margin-bottom: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.auth-form input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.auth-btn {
  width: 100%;
  padding: 14px 18px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, transform 0.05s;
}

.auth-btn--primary {
  background: #2563eb;
  color: #fff;
}

.auth-btn--primary:hover {
  background: #1d4ed8;
}

.auth-switch {
  margin-top: 20px;
  font-size: 14px;
  color: #6b7280;
  text-align: center;
}

.auth-switch a {
  color: #2563eb;
  font-weight: 600;
  text-decoration: none;
}

.auth-switch a:hover {
  text-decoration: underline;
}

.auth-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0;
  color: #9ca3af;
  font-size: 12px;
  font-weight: 600;
}

.auth-divider::before,
.auth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.google-mount {
  display: flex;
  justify-content: center;
  min-height: 44px;
}

.google-mount :deep(div[role='button']) {
  width: 100% !important;
}

.auth-hint-muted {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
  margin: 8px 0 0;
}

.forgot-password-toggle {
  margin-top: 20px;
  text-align: center;
}

.link-button {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
}

.link-button:hover {
  color: #111827;
  text-decoration: underline;
}

.forgot-password-block {
  margin-top: 12px;
  padding: 12px 14px;
  background: #f9fafb;
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  text-align: left;
}

.forgot-password-block a {
  color: #2563eb;
}
</style>
